import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    FormControlLabel,
    Switch,
    CircularProgress,
    FormLabel,
    FormGroup,
} from '@mui/material';
import { Feature } from '../types';
import { ChangeEvent, useEffect, useState } from 'react';

interface EditFeatureModalProps {
    open: boolean;
    feature: Feature;
    onClose: () => void;
    onSave: (newConfig: Feature['configuration'], reason: string) => Promise<void>;
    loading?: boolean;
}

export const BooleanEditModal = ({
    open,
    feature,
    onClose,
    onSave,
    loading,
}: EditFeatureModalProps) => {
    const [enabled, setEnabled] = useState(feature.configuration.enabled ?? false);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setEnabled(feature.configuration.enabled ?? false);
            setReason('');
        }
    }, [open, feature]);

    const handleSave = async () => {
        await onSave(
            {
                type: 'boolean',
                enabled,
            },
            reason,
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier la fonctionnalité</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={enabled}
                                onChange={(e) => setEnabled(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label={enabled ? 'Activé' : 'Désactivé'}
                    />
                    <FormGroup>
                        <FormLabel>Raison de la modification</FormLabel>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                            placeholder="Expliquez pourquoi vous modifiez cette fonctionnalité..."
                        />
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" color="secondary" onClick={onClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const LimitEditModal = ({
    open,
    feature,
    onClose,
    onSave,
    loading,
}: EditFeatureModalProps) => {
    const [limit, setLimit] = useState(String(feature.configuration.limit ?? 0));
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setLimit(String(feature.configuration.limit ?? 0));
            setReason('');
        }
    }, [open, feature]);

    const parsedValue = Number(limit);
    const isValid = limit.trim() !== '' && Number.isFinite(parsedValue) && parsedValue >= 0;

    const handleSave = async () => {
        if (!isValid) return;
        await onSave(
            {
                type: 'limit',
                limit: parsedValue,
            },
            reason,
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier la fonctionnalité</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <FormGroup>
                        <FormLabel>Limite</FormLabel>
                        <TextField
                            type="number"
                            fullWidth
                            value={limit}
                            onChange={(e) => setLimit(e.target.value)}
                            disabled={loading}
                            inputProps={{ min: 0 }}
                            error={limit.trim() !== '' && !isValid}
                            helperText={limit.trim() !== '' && !isValid ? 'Veuillez saisir un nombre valide.' : ''}
                        />
                    </FormGroup>

                    <FormGroup>
                        <FormLabel>Raison de la modification</FormLabel>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                            placeholder="Expliquez pourquoi vous modifiez cette fonctionnalité..."
                        />
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" color="secondary" onClick={onClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isValid}
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const DurationEditModal = ({
    open,
    feature,
    onClose,
    onSave,
    loading,
}: EditFeatureModalProps) => {
    const [duration, setDuration] = useState(String(feature.configuration.duration ?? 0));
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setDuration(String(feature.configuration.duration ?? 0));
            setReason('');
        }
    }, [open, feature]);

    const parsedValue = Number(duration);
    const isValid = duration.trim() !== '' && Number.isFinite(parsedValue) && parsedValue >= 0;

    const handleSave = async () => {
        if (!isValid) return;
        await onSave(
            {
                type: 'duration',
                duration: parsedValue,
                unit: feature.configuration.unit,
            },
            reason,
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier la fonctionnalité</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <FormGroup>
                        <FormLabel>Durée {feature.configuration.unit ? `(${feature.configuration.unit})` : ''}</FormLabel>
                        <TextField
                            type="number"
                            fullWidth
                            value={duration}
                            onChange={(e) => setDuration(e.target.value)}
                            disabled={loading}
                            inputProps={{ min: 0 }}
                            error={duration.trim() !== '' && !isValid}
                            helperText={duration.trim() !== '' && !isValid ? 'Veuillez saisir un nombre valide.' : ''}
                        />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Raison de la modification</FormLabel>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                            placeholder="Expliquez pourquoi vous modifiez cette fonctionnalité..."
                        />
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" color="secondary" onClick={onClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isValid}
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const CreditsEditModal = ({
    open,
    feature,
    onClose,
    onSave,
    loading,
}: EditFeatureModalProps) => {
    const initialValue = feature.configuration.maximum ?? 0;
    const [maximum, setMaximum] = useState(String(initialValue));
    const [isUnlimited, setIsUnlimited] = useState(initialValue === -1);
    const [lastFiniteValue, setLastFiniteValue] = useState(initialValue === -1 ? 0 : initialValue);
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setMaximum(String(initialValue));
            setIsUnlimited(initialValue === -1);
            if (initialValue !== -1) {
                setLastFiniteValue(initialValue);
            }
            setReason('');
        }
    }, [open, feature, initialValue]);

    const parsedValue = Number(maximum);
    const effectiveValue = isUnlimited ? -1 : parsedValue;
    const isValid = isUnlimited || (maximum.trim() !== '' && Number.isFinite(parsedValue) && parsedValue >= 0);

    const handleUnlimitedToggle = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (checked) {
            if (Number.isFinite(parsedValue) && parsedValue >= 0) {
                setLastFiniteValue(parsedValue);
            }
            setMaximum('-1');
            setIsUnlimited(true);
            return;
        }
        setMaximum(String(lastFiniteValue));
        setIsUnlimited(false);
    };

    const handleSave = async () => {
        if (!isValid) return;
        await onSave(
            {
                type: 'credits',
                maximum: effectiveValue,
            },
            reason,
        );
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Modifier la fonctionnalité</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <FormGroup>
                        <FormLabel sx={{ mb: 0 }}>Crédits maximum</FormLabel>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <TextField
                                type="number"
                                fullWidth
                                value={maximum}
                                onChange={(e) => setMaximum(e.target.value)}
                                disabled={loading || isUnlimited}
                                inputProps={{ min: 0 }}
                                error={!isUnlimited && maximum.trim() !== '' && !isValid}
                                helperText={
                                    !isUnlimited && maximum.trim() !== '' && !isValid
                                        ? 'Veuillez saisir un nombre positif.'
                                        : ''
                                }
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isUnlimited}
                                        onChange={handleUnlimitedToggle}
                                        disabled={loading}
                                    />
                                }
                                label="Illimité"
                            />
                        </Stack>
                    </FormGroup>
                    <FormGroup>
                        <FormLabel>Raison de la modification</FormLabel>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={loading}
                            placeholder="Expliquez pourquoi vous modifiez cette fonctionnalité..."
                        />
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" color="secondary" onClick={onClose} disabled={loading}>
                    Annuler
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !isValid}
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};
